import { common, JSONHelper, scriptHashToAddress, VMState, } from '@neo-one/client-common';
import { utils } from '@neo-one/utils';
import BigNumber from 'bignumber.js';
import BN from 'bn.js';
export function convertCallReceipt(receipt) {
    return {
        result: convertInvocationResult(receipt.result),
        actions: receipt.actions.map((action, idx) => convertAction('0x​​​​​0000000000000000000000000000000000000000000000000000000000000000​​​​​', 0, '0x​​​​​0000000000000000000000000000000000000000000000000000000000000000​​​​​', 0, idx, action)),
    };
}
export function convertAction(blockHash, blockIndex, transactionHash, transactionIndex, index, action) {
    if (action.type === 'Log') {
        return {
            type: 'Log',
            version: action.version,
            blockIndex,
            blockHash,
            transactionIndex,
            transactionHash,
            index,
            globalIndex: JSONHelper.readUInt64(action.index),
            address: scriptHashToAddress(action.scriptHash),
            message: action.message,
        };
    }
    return {
        type: 'Notification',
        version: action.version,
        blockIndex,
        blockHash,
        transactionIndex,
        transactionHash,
        index,
        globalIndex: JSONHelper.readUInt64(action.index),
        address: scriptHashToAddress(action.scriptHash),
        args: convertContractParameters(action.args),
    };
}
export function convertInvocationResult(result) {
    if (result.state === VMState.Fault) {
        return {
            state: 'FAULT',
            gasConsumed: new BigNumber(result.gas_consumed),
            gasCost: new BigNumber(result.gas_cost),
            stack: convertContractParameters(result.stack),
            message: result.message,
        };
    }
    return {
        state: 'HALT',
        gasConsumed: new BigNumber(result.gas_consumed),
        gasCost: new BigNumber(result.gas_cost),
        stack: convertContractParameters(result.stack),
    };
}
export function convertContractParameters(parameters) {
    return parameters.map(convertContractParameter);
}
export function convertContractParameter(parameter) {
    switch (parameter.type) {
        case 'Array':
            return {
                type: 'Array',
                value: convertContractParameters(parameter.value),
            };
        case 'Boolean':
            return parameter;
        case 'ByteArray':
            return {
                type: 'Buffer',
                value: parameter.value,
            };
        case 'Hash160':
            return {
                type: 'Address',
                value: scriptHashToAddress(parameter.value),
            };
        case 'Hash256':
            return parameter;
        case 'Integer':
            return {
                type: 'Integer',
                value: new BN(parameter.value, 10),
            };
        case 'InteropInterface':
            return parameter;
        case 'Map':
            return {
                type: 'Map',
                value: parameter.value.map(([key, val]) => [convertContractParameter(key), convertContractParameter(val)]),
            };
        case 'PublicKey':
            return parameter;
        case 'Signature':
            return parameter;
        case 'String':
            return parameter;
        case 'Void':
            return parameter;
        default:
            utils.assertNever(parameter);
            throw new Error('For TS');
    }
}
export function convertContractParameterType(param) {
    switch (param) {
        case 'Signature':
            return 'Signature';
        case 'Boolean':
            return 'Boolean';
        case 'Integer':
            return 'Integer';
        case 'Hash160':
            return 'Address';
        case 'Hash256':
            return 'Hash256';
        case 'ByteArray':
            return 'Buffer';
        case 'PublicKey':
            return 'PublicKey';
        case 'String':
            return 'String';
        case 'Array':
            return 'Array';
        case 'Map':
            return 'Map';
        case 'InteropInterface':
            return 'InteropInterface';
        case 'Void':
            return 'Void';
        default:
            utils.assertNever(param);
            throw new Error('For TS');
    }
}
export function convertStorageItem(storageItem) {
    return {
        address: scriptHashToAddress(storageItem.hash),
        key: storageItem.key,
        value: storageItem.value,
    };
}
export function convertBlock(block, convertTransactionHelp) {
    return {
        version: block.version,
        hash: block.hash,
        previousBlockHash: block.previousblockhash,
        merkleRoot: block.merkleroot,
        time: block.time,
        index: block.index,
        nonce: block.nonce,
        nextConsensus: block.nextconsensus,
        script: block.script,
        size: block.size,
        transactions: block.tx.map((transaction) => convertTransactionHelp(transaction, block)),
    };
}
export function convertAssetType(assetType) {
    switch (assetType) {
        case 'CreditFlag':
            return 'Credit';
        case 'DutyFlag':
            return 'Duty';
        case 'GoverningToken':
            return 'Governing';
        case 'UtilityToken':
            return 'Utility';
        case 'Currency':
            return 'Currency';
        case 'Share':
            return 'Share';
        case 'Invoice':
            return 'Invoice';
        case 'Token':
            return 'Token';
        default:
            utils.assertNever(assetType);
            throw new Error('For TS');
    }
}
export function convertAsset(asset) {
    const assetName = asset.name;
    let name;
    if (Array.isArray(assetName)) {
        const enName = assetName.find(({ lang }) => lang === 'en');
        name = enName === undefined ? assetName[0].name : enName.name;
    }
    else {
        name = assetName;
    }
    return {
        hash: asset.id,
        type: convertAssetType(asset.type),
        name,
        amount: new BigNumber(asset.amount),
        available: new BigNumber(asset.available),
        precision: asset.precision,
        owner: asset.owner,
        admin: asset.admin,
        issuer: asset.issuer,
        expiration: asset.expiration,
        frozen: asset.frozen,
    };
}
export function convertContract(contractIn) {
    let contract = contractIn;
    if (typeof contract.version === 'string' && contract.version.includes('preview')) {
        contract = convertPreviewContract(contract);
    }
    return {
        version: contract.version,
        address: scriptHashToAddress(contract.hash),
        script: contract.script,
        parameters: contract.parameters.map(convertContractParameterType),
        returnType: convertContractParameterType(contract.returntype),
        name: contract.name,
        codeVersion: contract.code_version,
        author: contract.author,
        email: contract.email,
        description: contract.description,
        storage: contract.properties.storage,
        dynamicInvoke: contract.properties.dynamic_invoke,
        payable: contract.properties.payable,
    };
}
export function convertPreviewContract(contract) {
    return {
        version: 0,
        hash: contract.code.hash,
        script: contract.code.script,
        parameters: contract.code.parameters,
        returntype: contract.code.returntype,
        name: contract.name,
        code_version: 'preview',
        author: contract.author,
        email: contract.email,
        description: contract.description,
        properties: {
            storage: contract.needstorage,
            dynamic_invoke: false,
            payable: false,
        },
    };
}
export function convertInputs(inputs) {
    return inputs.map(convertInput);
}
export function convertInput(input) {
    return {
        hash: input.txid,
        index: input.vout,
    };
}
export function convertOutputs(outputs) {
    return outputs.map(convertOutput);
}
export function convertOutput(output) {
    return {
        asset: output.asset,
        address: output.address,
        value: new BigNumber(output.value),
    };
}
export function convertAttributes(attributes) {
    return attributes.map((attribute) => ({
        usage: attribute.usage,
        data: attribute.usage === 'Script' ? scriptHashToAddress(attribute.data) : attribute.data,
    }));
}
export function convertTransactionBase(transaction, convertInvocation, convertTransactionHelp) {
    const transactionBase = {
        hash: transaction.txid,
        size: transaction.size,
        version: transaction.version,
        attributes: convertAttributes(transaction.attributes),
        inputs: convertInputs(transaction.vin),
        outputs: convertOutputs(transaction.vout),
        scripts: transaction.scripts,
        systemFee: new BigNumber(transaction.sys_fee),
        networkFee: new BigNumber(transaction.net_fee),
    };
    let converted;
    switch (transaction.type) {
        case 'ClaimTransaction':
            converted = {
                ...transactionBase,
                type: 'ClaimTransaction',
                claims: convertInputs(transaction.claims),
            };
            break;
        case 'ContractTransaction':
            converted = {
                ...transactionBase,
                type: 'ContractTransaction',
            };
            break;
        case 'EnrollmentTransaction':
            converted = {
                ...transactionBase,
                type: 'EnrollmentTransaction',
                publicKey: transaction.pubkey,
            };
            break;
        case 'InvocationTransaction':
            return convertInvocation(transaction, transactionBase);
        case 'IssueTransaction':
            converted = {
                ...transactionBase,
                type: 'IssueTransaction',
            };
            break;
        case 'MinerTransaction':
            converted = {
                ...transactionBase,
                type: 'MinerTransaction',
                nonce: transaction.nonce,
            };
            break;
        case 'PublishTransaction':
            converted = {
                ...transactionBase,
                type: 'PublishTransaction',
                contract: convertContract(transaction.contract),
            };
            break;
        case 'RegisterTransaction':
            converted = {
                ...transactionBase,
                type: 'RegisterTransaction',
                asset: {
                    type: convertAssetType(transaction.asset.type),
                    name: Array.isArray(transaction.asset.name)
                        ? transaction.asset.name[0].name
                        : transaction.asset.name,
                    amount: new BigNumber(transaction.asset.amount),
                    precision: transaction.asset.precision,
                    owner: transaction.asset.owner,
                    admin: transaction.asset.admin,
                },
            };
            break;
        case 'StateTransaction':
            converted = {
                ...transactionBase,
                type: 'StateTransaction',
            };
            break;
        default:
            utils.assertNever(transaction);
            throw new Error('For TS');
    }
    return convertTransactionHelp(converted);
}
export function convertTransaction(transaction) {
    return convertTransactionBase(transaction, (invocation, transactionBase) => ({
        ...transactionBase,
        type: 'InvocationTransaction',
        script: invocation.script,
        gas: new BigNumber(invocation.gas),
    }), (converted) => converted);
}
export function convertConfirmedTransaction(transaction) {
    if (transaction.data === undefined) {
        throw new Error('Unexpected undefined data');
    }
    const data = {
        blockHash: transaction.data.blockHash,
        blockIndex: transaction.data.blockIndex,
        transactionIndex: transaction.data.transactionIndex,
        globalIndex: JSONHelper.readUInt64(transaction.data.globalIndex),
    };
    return convertTransactionBase(transaction, (invocation, transactionBase) => {
        if (invocation.invocationData === undefined || transaction.data === undefined) {
            throw new Error('Unexpected undefined data');
        }
        const invocationData = convertInvocationData(invocation.invocationData, transaction.data.blockHash, transaction.data.blockIndex, transaction.txid, transaction.data.transactionIndex);
        return {
            ...transactionBase,
            type: 'InvocationTransaction',
            script: invocation.script,
            gas: new BigNumber(invocation.gas),
            receipt: data,
            invocationData,
        };
    }, (converted) => ({ ...converted, receipt: data }));
}
export function convertInvocationData(data, blockHash, blockIndex, transactionHash, transactionIndex) {
    return {
        result: convertInvocationResult(data.result),
        asset: data.asset === undefined ? data.asset : convertAsset(data.asset),
        contracts: data.contracts.map(convertContract),
        deletedContractAddresses: data.deletedContractHashes.map(scriptHashToAddress),
        migratedContractAddresses: data.migratedContractHashes.map(([hash0, hash1]) => [scriptHashToAddress(hash0), scriptHashToAddress(hash1)]),
        actions: data.actions.map((action, idx) => convertAction(blockHash, blockIndex, transactionHash, transactionIndex, idx, action)),
        storageChanges: data.storageChanges.map(convertStorageChange),
    };
}
export function convertStorageChange(storageChange) {
    if (storageChange.type === 'Add') {
        return {
            type: 'Add',
            address: scriptHashToAddress(storageChange.hash),
            key: storageChange.key,
            value: storageChange.value,
        };
    }
    if (storageChange.type === 'Modify') {
        return {
            type: 'Modify',
            address: scriptHashToAddress(storageChange.hash),
            key: storageChange.key,
            value: storageChange.value,
        };
    }
    return {
        type: 'Delete',
        address: scriptHashToAddress(storageChange.hash),
        key: storageChange.key,
    };
}
export function convertRelayTransactionResult(result) {
    const transaction = convertTransaction(result.transaction);
    const verifyResult = result.verifyResult === undefined ? undefined : convertVerifyResult(transaction.hash, result.verifyResult);
    return { transaction, verifyResult };
}
export function convertVerifyResult(transactionHash, result) {
    return {
        verifications: result.verifications.map((verification) => ({
            failureMessage: verification.failureMessage,
            witness: verification.witness,
            address: scriptHashToAddress(verification.hash),
            actions: verification.actions.map((action, idx) => convertAction(common.uInt256ToString(common.bufferToUInt256(Buffer.alloc(32, 0))), -1, transactionHash, -1, idx, action)),
        })),
    };
}
export function convertNetworkSettings(settings) {
    return {
        issueGASFee: new BigNumber(settings.issueGASFee),
    };
}

//# sourceMappingURL=convert.js.map
